import * as moment from 'moment';
import Constants, {AnnotationModes, ModalStates, PrivacyTypes, SortMetric, MobileViewingModes, NotificationTypes, QuizState, DashboardFilter} from '../globals/Constants';
import {nullVideoPlayerFactory} from '../types/IVideoPlayer';
import LocalStorage from '../services/LocalStorageWrapper/LocalStorageWrapper';
import AuthorizedActionsModel, {nullAuthorizedActionModel} from '../types/AuthorizedActionsModel';
import QuotasModel, {nullQuotasModel} from '../types/QuotasModel';
import AuthorizationModel from '../types/AuthorizationsModel';
import {filterNotificationSetting, nullNotificationSettingsModelFactory} from '../types/NotificationSettingsModel';
import * as clone from 'clone';
import ReviewModel from '../types/ReviewModel';
import CommentModel from '../types/CommentModel';
import UsersModel from '../types/UsersModel';
import ReduxState from '../types/ReduxState';
import MarkerModel from '../types/MarkerModel';
import AuthorizationsResponseModel from '../types/AuthorizationsResponseModel';
import UserModel, {nullUserFactory} from '../types/UserModel';
import ReviewVersionModel from '../types/ReviewVersionModel';

const commentSort = (a: CommentModel, b: CommentModel): number => {
   return a.time - b.time || Date.parse(a.createDate) - Date.parse(b.createDate);
};

const getNormalizedCommentState = (comments: CommentModel[], updatedComments: CommentModel[], selectedComment: CommentModel, users: UsersModel,
                                   searchText: string, hideResolved: boolean, versionId: string, playTime: number): any => {
   comments = combineComments(comments, updatedComments);
   comments = setUsersForComments(comments, users);

   const topLevelComments = getVisibleRootCommentsAndReplies(comments, searchText, hideResolved, versionId);
   const visibleComments = getVisibleAllCommentsAndReplies(comments, searchText, hideResolved, versionId);
   const markers = markersFromComments(visibleComments);
   selectedComment = getCommentToSelectByTime(playTime, topLevelComments, hideResolved, selectedComment);
   return {comments, markers, visibleComments, topLevelComments, selectedComment: selectedComment};
};

const markersFromComments = (comments: CommentModel[]): MarkerModel[] => {
   let time = -1;
   const markers: MarkerModel[] = [];
   comments.filter(c => !c.parentCommentId).forEach(c => {
      if (time !== c.time) {
         time = c.time;
         markers.push({commentId: c.id, time: c.time, authorName: c.user && c.user.name, commentText: c.text, hasAnnotation: !!c.annotation});
      } else if (c.annotation) {
         markers[markers.length - 1].hasAnnotation = true;
      }
   });
   return markers;
};

const searchCommentTextAndName = (searchText: string, comment: CommentModel) => {
   const searchRegex = new RegExp(searchText.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'), 'i');
   return searchRegex.test(comment.text) || (comment.user && comment.user.name && searchRegex.test(comment.user.name));
};

const getVisibleRootComments = (comments: CommentModel[], searchText: string, hideResolved: boolean, reviewId: string) => {
   let rootComments = comments.filter(c => !c.parentCommentId && c.reviewId === reviewId);

   if (comments.length === 0) {
      return rootComments;
   }

   if (hideResolved) {
      rootComments = rootComments.filter(c => !c.isResolved);
   }

   if (searchText) {
      rootComments = rootComments.filter(comment => {
         if (searchCommentTextAndName(searchText, comment)) {
            return true;
         }

         const replies = comments.filter(r => r.parentCommentId === comment.id);
         return replies.some(reply => {
            return searchCommentTextAndName(searchText, reply);
         });
      });
   }

   return rootComments;
};

const getVisibleRootCommentsAndReplies = (comments: CommentModel[], searchText: string, hideResolved: boolean, reviewId: string) => {
   const visibleRootComments = getVisibleRootComments(comments, searchText, hideResolved, reviewId);
   return visibleRootComments.map(c => {
      return Object.assign({}, c, {replies: comments.filter(r => r.parentCommentId === c.id)});
   });
};

const getVisibleAllCommentsAndReplies = (comments: CommentModel[], searchText: string, hideResolved: boolean, reviewId: string) => {
   const visibleRootComments = getVisibleRootComments(comments, searchText, hideResolved, reviewId);
   return visibleRootComments.concat(comments.filter(r => visibleRootComments.find(c => c.id === r.parentCommentId)));
};

const getCommentToSelectByTime = (time: number, comments: CommentModel[], hideResolved: boolean, currentlySelectedComment: CommentModel): CommentModel => {
   if (currentlySelectedComment) {
      currentlySelectedComment = comments.find(c => c.id === currentlySelectedComment.id) || currentlySelectedComment;
   }

   comments = getUniqueTimesWithComments(comments);

   let commentToSelectByTime = null;
   const timeWithTolerance = time + Constants.annotation.timeTolerance;
   for (const c of comments) {
      if (c.time <= timeWithTolerance) {
         if (!commentToSelectByTime || commentToSelectByTime.time !== c.time) {
            commentToSelectByTime = c;
         }
      }
   }

   if (currentlySelectedComment && commentToSelectByTime && currentlySelectedComment.time === commentToSelectByTime.time) {
      return currentlySelectedComment;
   }

   return commentToSelectByTime || comments[0] || ({} as CommentModel);
};

const getUniqueTimesWithComments = (comments: CommentModel[]): CommentModel[] => {
   const uniqueTimes: number[] = [];
   const uniqueComments: CommentModel[] = [];
   comments.forEach(c => {
      if (uniqueTimes.indexOf(c.time) === -1) {
         uniqueTimes.push(c.time);
         uniqueComments.push(c);
      }
   });

   return uniqueComments;
};

const combineComments = (comments: CommentModel[], commentsToAdd: CommentModel[]): CommentModel[] => {
   let commentsToReturn: CommentModel[] = clone(comments);

   commentsToAdd.forEach(comment => {
      const index = commentsToReturn.findIndex(c => c.id === comment.id);
      const commentShouldBeVisible = shouldCommentBeVisible(comment);

      if (index === -1) {
         if (commentShouldBeVisible) {
            commentsToReturn.push(comment);
         }
      } else {
         if (commentShouldBeVisible) {
            commentsToReturn[index] = comment;
         } else {
            commentsToReturn.splice(index, 1);
         }
      }
   });

   commentsToAdd.forEach(comment => {
      if (comment.parentCommentId) {
         const parentComment = clone(commentsToReturn.find(c => c.id === comment.parentCommentId));
         if (parentComment) {
            parentComment.replies = combineComments(parentComment.replies, [comment]);
            commentsToReturn = combineComments(commentsToReturn, [parentComment]);
         }
      }
   });

   return commentsToReturn.sort(commentSort);
};

const shouldCommentBeVisible = (comment: CommentModel) => {
   if (!comment.isDeleted) {
      return true;
   }
   if (comment.replies && comment.replies.some(reply => !reply.isDeleted)) {
      return true;
   }
   return false;
};

const loadResolutionFilter = (reviewId: string): boolean => {
   return (LocalStorage.getItem('ResolutionFilter_' + reviewId) === 'true');
};

const startingCharCode = 65; // Capital A
const alphabetLength = 26;

const getDeletedUserSymbol = (deletedUserIndex: number): string => {
   let userSymbol = '';
   let quotient = deletedUserIndex;

   do {
      const remainder = quotient % alphabetLength;
      quotient = Math.floor(quotient / alphabetLength) - 1;

      userSymbol = String.fromCharCode(startingCharCode + remainder) + userSymbol;
   } while (quotient >= 0);

   return userSymbol;
};

const getOwnedReviewsCount = (reviews: ReviewModel[], userId: string): number => {
   return reviews.filter(review => review.Reviewers.some(reviewer => reviewer.Id === userId && reviewer.Role === Constants.reviewerRoles.owner)).length;
};

const setUsersForComments = (comments: CommentModel[], users: UsersModel): CommentModel[] => {
   const commentsToReturn: CommentModel[] = clone(comments);

   commentsToReturn.forEach(comment => {
      comment.user = users[comment.authorId];
      comment.replies.forEach(reply => {
         reply.user = users[reply.authorId];
      });
   });

   return commentsToReturn;
};

const hasOwnerPermissions = (currentUserId: string, review: ReviewModel): boolean => {
   return currentUserId && review?.Reviewers?.some(reviewer => reviewer.Id === currentUserId && (reviewer.Role === Constants.reviewerRoles.owner || reviewer.Role === Constants.reviewerRoles.collaborator));
};

const defaultState: ReduxState = {
   comments: [],
   users: {},
   commentNotificationSettings: nullNotificationSettingsModelFactory(),
   replyNotificationSettings: nullNotificationSettingsModelFactory(),
   reviews: [],
   filteredReviews: [],
   searchedReviews: [],
   reviewerGroups: [],
   selectedComment: {} as CommentModel,
   annotationMode: AnnotationModes.none,
   reviewsLoaded: false,
   player: nullVideoPlayerFactory(),
   authorization: {} as AuthorizationModel,
   dashboardFilter: DashboardFilter.all,
   ownedReviewsCount: 0,
   pageIsLoading: false,
   isCommentHotkeyEnabled: false,
   themeName: Constants.themeName.default,
   currentUser: nullUserFactory(),
   reviewsSortMetric: SortMetric.createdDateDescending,
   currentReviewVersionNumber: 0,
   mobileViewingMode: MobileViewingModes.conversation,
   quizState: QuizState.closed,
   totalReviewsCount: 0,
   totalOwnedReviewsCount: 0,
   totalNotOwnedReviewsCount: 0
};

const getSortedReviews = (reviews: ReviewModel[], reviewsSortMetric: SortMetric): ReviewModel[] => {
   return reviews?.sort((review1, review2) => {
      switch (reviewsSortMetric) {
         case SortMetric.createdDateAscending: {
            return (new Date(review1.ReviewVersions.filter(ver => !ver.IsDeleted).pop().CreatedDate) as any) - (new Date(review2.ReviewVersions.filter(ver => !ver.IsDeleted).pop().CreatedDate) as any);
         }
         case SortMetric.createdDateDescending: {
            return (new Date(review2.ReviewVersions.filter(ver => !ver.IsDeleted).pop().CreatedDate) as any) - (new Date(review1.ReviewVersions.filter(ver => !ver.IsDeleted).pop().CreatedDate) as any);
         }
         case SortMetric.titleAscending: {
            return review1.Title.localeCompare(review2.Title);
         }
         case SortMetric.titleDescending: {
            return review2.Title.localeCompare(review1.Title);
         }
         default: {
            return (new Date(review1.ReviewVersions.filter(ver => !ver.IsDeleted).pop().CreatedDate) as any) - (new Date(review2.ReviewVersions.filter(ver => !ver.IsDeleted).pop().CreatedDate) as any);
         }
      }
   });
};

const getFilteredReviews = (reviews: ReviewModel[], dashboardFilter: DashboardFilter, searchText: string, currentUserId: string): ReviewModel[] => {
   let filteredReviews: ReviewModel[] = reviews;

   if (dashboardFilter === DashboardFilter.owned) {
      filteredReviews = reviews?.filter(review => review.Reviewers.some(reviewer => reviewer.Id === currentUserId && reviewer.Role === Constants.reviewerRoles.owner));
   }

   if (dashboardFilter === DashboardFilter.others) {
      filteredReviews = reviews.filter(review => {
         if (review.Reviewers.some(reviewer => reviewer.Id === currentUserId && reviewer.Role === Constants.reviewerRoles.owner)) {
            return false;
         }

         return review?.Reviewers.some(reviewer => reviewer.Id === currentUserId && reviewer.Role === Constants.reviewerRoles.reviewer);
      });
   }

   if (searchText) {
      const reviewsSearchRegex = new RegExp(searchText.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'), 'i');
      filteredReviews = filteredReviews?.filter(review => reviewsSearchRegex.test(review.Title));
   }

   return filteredReviews;
};

export default (state: ReduxState = defaultState, action: any): ReduxState => {
   switch (action.type) {
      case 'ADD_OR_UPDATE_USER': {
         const user: UserModel = action.user;
         const users = clone(state.users);
         users[user.userId] = user;
         return Object.assign({}, state, {users: users});
      }
      case 'SET_USERID': {
         const currentUserId = action.userId;
         const currentUser = state.users[currentUserId];
         return Object.assign({}, state, {
            currentUser: currentUser,
            currentUserId: currentUserId
         }, state.currentReview && {
            isOwner: hasOwnerPermissions(currentUserId, state.currentReview)
         });
      }
      case 'SET_USERS': {
         const users: UsersModel = action.users;
         const currentReview = state.reviews.find(review => review.Id === state.reviewId);
         let topLevelComments: CommentModel[] = [];
         let visibleComments: CommentModel[] = [];

         const currentUser = state.currentUserId && users[state.currentUserId];
         const comments = [...state.comments].map(c => {
            return Object.assign({}, c, {user: users[c.authorId]});
         });

         if (currentReview) {
            const deletedUsers = currentReview.Reviewers.filter(reviewer => reviewer.IsUserDeleted);

            // Do not double count the same user with different roles
            const uniqueDeletedUsers = [...new Set(deletedUsers.map(item => item.Id))];

            uniqueDeletedUsers.forEach((deletedUserId: string, i: number) => {
               users[deletedUserId].name = Constants.user.deletedName + ' (' + getDeletedUserSymbol(i) + ')';
            });
            topLevelComments = getVisibleRootCommentsAndReplies(comments, state.searchText, state.hideResolved, state.currentReviewVersion.Id);
            visibleComments = getVisibleAllCommentsAndReplies(comments, state.searchText, state.hideResolved, state.currentReviewVersion.Id);
         }

         return Object.assign({}, state, {
            users,
            currentUser,
            comments,
            visibleComments,
            topLevelComments
         });
      }
      case 'SET_SELECTED_COMMENT': {
         const selectedComment = state.comments.find(c => c.id === action.id);
         return Object.assign({}, state, {selectedComment: selectedComment || {}, hideAnnotation: false});
      }
      case 'SET_PLAYER': {
         return Object.assign({}, state, {player: action.player});
      }
      case 'SET_ANNOTATION_COLOR': {
         return Object.assign({}, state, {annotationColor: action.annotationColor});
      }
      case 'SET_CANVAS': {
         return Object.assign({}, state, {canvas: action.canvas});
      }
      case 'SET_ANNOTATION_MODE': {
         return Object.assign({}, state, {annotationMode: action.annotationMode});
      }
      case 'SET_MOBILE_VIEWING_MODE': {
         return Object.assign({}, state, {mobileViewingMode: action.mobileViewingMode});
      }
      case 'SET_HIDE_ANNOTATION': {
         return Object.assign({}, state, {hideAnnotation: action.hideAnnotation});
      }
      case 'SET_THEME_NAME': {
         return Object.assign({}, state, {themeName: action.themeName});
      }
      case 'SET_PLAYER_PROGRESS': {
         if (action.playTime === state.playTime) {
            return state;
         }
         const stateToAssign: any = {playTime: action.playTime};
         const {hideResolved, selectedComment, topLevelComments} = state;
         if (action.playTime !== state.playTime && state.comments.length > 0) {
            const commentToSelect = getCommentToSelectByTime(action.playTime, topLevelComments, hideResolved, selectedComment);
            if (commentToSelect) {
               if (!selectedComment || selectedComment.time == null || Math.abs(selectedComment.time - action.playTime) > Constants.annotation.timeTolerance) {
                  stateToAssign.selectedComment = commentToSelect;
                  stateToAssign.hideAnnotation = false;
               }
            } else {
               stateToAssign.selectedComment = {};
            }
         }
         return Object.assign({}, state, stateToAssign);
      }
      case 'RESET_REVIEW': {
         return Object.assign({}, state, {
            annotationMode: AnnotationModes.none,
            playTime: 0,
            playDuration: 0,
            selectedComment: {},
            player: nullVideoPlayerFactory(),
            canvas: null,
            searchText: '',
            visibleComments: [],
            topLevelComments: [],
            comments: [],
            mostRecentCommentUpdate: null,
            hideAnnotation: false,
            mediaDimensions: null
         });
      }
      case 'SET_COMMENTS': {
         const stateToAssign: Partial<ReduxState> = getNormalizedCommentState(state.comments, action.comments, state.selectedComment, state.users,
            state.searchText, state.hideResolved, state.currentReviewVersion.Id, state.playTime);

         const mostRecentLastModifiedDate = Math.max(null, ...stateToAssign.comments.map(comment => new Date(comment.synchTimeStamp).getTime()));
         stateToAssign.mostRecentCommentUpdate = mostRecentLastModifiedDate ? new Date(mostRecentLastModifiedDate) : null;

         if (state.selectedComment && state.selectedComment.id) {
            stateToAssign.selectedComment = state.selectedComment;
         }

         return Object.assign({}, state, stateToAssign);
      }
      case 'SET_MODAL_STATE': {
         const {modalState} = action.modalModel;
         const stateToAssign = {modalState: modalState, modalEventHandlers: action.modalEventHandlersModel};

         return Object.assign({}, state, stateToAssign);
      }
      case 'BATCH_ADD_REVIEWS': {
         let reviews: ReviewModel[] = clone(state.reviews);
         let filteredReviews: ReviewModel[] = clone(state.filteredReviews);
         let searchedReviews: ReviewModel[] = clone(state.searchedReviews);
         action.reviewsToAdd.forEach((review: ReviewModel) => {
            if (reviews.every(existingReview => existingReview.Id !== review.Id)) {
               reviews.push(review);
            }
         });
         reviews = getSortedReviews(reviews, state.reviewsSortMetric);
         filteredReviews = state.dashboardFilter === DashboardFilter.all ? reviews : getFilteredReviews(reviews, state.dashboardFilter, '', state.currentUserId);
         searchedReviews = state.reviewSearchText ? getFilteredReviews(filteredReviews, state.dashboardFilter, state.reviewSearchText, state.currentUserId) : searchedReviews;
         return Object.assign({}, state, {reviews: reviews, filteredReviews: filteredReviews, searchedReviews: searchedReviews});
      }
      case 'RESET_REVIEWS': {
         return Object.assign({}, state, {reviews: [], filteredReviews: [], searchedReviews: []});
      }
      case 'ADD_OR_UPDATE_REVIEW': {
         let reviews: ReviewModel[] = clone(state.reviews);
         let filteredReviews: ReviewModel[] = clone(state.filteredReviews);
         let searchedReviews: ReviewModel[] = clone(state.searchedReviews);
         let totalReviewsCount: number = clone(state.totalReviewsCount);
         const index = reviews.findIndex(review => review.Id === action.review.Id);
         let originalPrivacyType;

         if (index === -1) {
            reviews.push(action.review);
            totalReviewsCount++;
            reviews = getSortedReviews(reviews, state.reviewsSortMetric);
            filteredReviews = getFilteredReviews(reviews, state.dashboardFilter, '', state.currentUserId);
            searchedReviews = getFilteredReviews(filteredReviews, state.dashboardFilter, state.reviewSearchText, state.currentUserId);
         } else {
            originalPrivacyType = reviews[index].PrivacyType;
            reviews[index] = action.review;
         }

         const ownedReviewsCount = getOwnedReviewsCount(reviews, state.currentUserId);

         const currentReview = state.reviewId && reviews.find(r => r.Id === state.reviewId);

         return Object.assign({}, state, {
            reviews: reviews,
            filteredReviews,
            searchedReviews,
            totalReviewsCount: totalReviewsCount,
            currentReview: currentReview,
            ownedReviewsCount: ownedReviewsCount,
            searchText: index === -1 ? '' : state.searchText
         }, currentReview && currentReview.PrivacyType === originalPrivacyType && {
            currentReviewVersion: currentReview.ReviewVersions.filter((ver: ReviewVersionModel) => !ver.IsDeleted).pop()
         });
      }
      case 'ADD_REVIEWERS': {
         const reviews: ReviewModel[] = clone(state.reviews);
         const index = reviews.findIndex(review => review.Id === action.id);

         if (index === -1) {
            return state;
         }

         reviews[index].Reviewers = [].concat(reviews[index].Reviewers).concat(action.newReviewers);
         const updateCurrentReview = state.currentReview && state.currentReview.Id === action.id;
         const newState = updateCurrentReview ? {reviews: reviews, currentReview: reviews[index]} : {reviews: reviews};
         return Object.assign({}, state, newState);
      }
      case 'ADD_COMMENT': {
         let commentToSelect: CommentModel = action.comment;
         if (action.comment.parentCommentId) {
            commentToSelect = state.comments.find(c => c.id === action.comment.parentCommentId);
         }

         const stateToAssign = getNormalizedCommentState(state.comments, [action.comment], commentToSelect, state.users,
            state.searchText, state.hideResolved, state.currentReviewVersion.Id, state.playTime);

         return Object.assign({}, state, stateToAssign);
      }
      case 'REMOVE_REVIEW': {
         let totalReviewsCount: number = clone(state.totalReviewsCount);
         const reviews: ReviewModel[] = clone(state.reviews);
         const filteredReviews: ReviewModel[] = clone(state.filteredReviews);
         const searchedReviews: ReviewModel[] = clone(state.searchedReviews);
         const index = reviews.findIndex(review => review.Id === action.id);
         const filteredReviewsIndex = filteredReviews.findIndex(review => review.Id === action.id);
         const searchedReviewsIndex = searchedReviews.findIndex(review => review.Id === action.id);

         if (index === -1) {
            return state;
         }

         reviews.splice(index, 1);
         filteredReviewsIndex !== -1 && filteredReviews.splice(filteredReviewsIndex, 1);
         searchedReviewsIndex !== -1 && searchedReviews.splice(searchedReviewsIndex, 1);
         totalReviewsCount--;
         const ownedReviewsCount = getOwnedReviewsCount(reviews, state.currentUserId);
         return Object.assign({}, state, {
            reviews: reviews,
            filteredReviews: filteredReviews,
            searchedReviews: searchedReviews,
            totalReviewsCount: totalReviewsCount,
            currentReview: state.reviewId && reviews.find(r => r.Id === state.reviewId),
            ownedReviewsCount: ownedReviewsCount
         });
      }
      case 'LEAVE_REVIEW': {
         let totalReviewsCount: number = clone(state.totalReviewsCount);
         let totalNotOwnedReviewsCount: number = clone(state.totalNotOwnedReviewsCount);
         const reviews: ReviewModel[] = clone(state.reviews);
         const filteredReviews: ReviewModel[] = clone(state.filteredReviews);
         const searchedReviews: ReviewModel[] = clone(state.searchedReviews);
         const index = reviews.findIndex(review => review.Id === action.id);
         const filteredIndex = filteredReviews.findIndex(review => review.Id === action.id);
         const searchedIndex = searchedReviews.findIndex(review => review.Id === action.id);

         if (index === -1) {
            return state;
         }

         totalReviewsCount--;
         totalNotOwnedReviewsCount--;
         reviews.splice(index, 1);
         filteredIndex !== -1 && filteredReviews.splice(filteredIndex, 1);
         searchedIndex !== -1 && searchedReviews.splice(searchedIndex, 1);
         return Object.assign({}, state, {
            reviews: reviews,
            filteredReviews: filteredReviews,
            searchedReviews: searchedReviews,
            totalReviewsCount: totalReviewsCount,
            totalNotOwnedReviewsCount: totalNotOwnedReviewsCount,
            currentReview: state.reviewId && reviews.find(r => r.Id === state.reviewId)
         });
      }
      case 'REMOVE_VERSION': {
         const reviews: ReviewModel[] = clone(state.reviews);
         const index = reviews.findIndex(review => review.Id === action.reviewId);
         const updatedReview = clone(state.currentReview);
         updatedReview.ReviewVersions[action.versionNumber - 1].IsDeleted = true;
         const updatedVersion = action.versionNumber === state.currentReviewVersion.VersionNumber ? updatedReview.ReviewVersions.filter((ver: ReviewVersionModel) => !ver.IsDeleted).pop() : state.currentReviewVersion;
         reviews[index].ReviewVersions[action.versionNumber - 1].IsDeleted = true;

         if (index > -1) {
            return Object.assign({}, state, {
               reviews: reviews,
               currentReview: updatedReview,
               currentReviewVersion: updatedVersion
            });
         }

         return state;
      }
      case 'SET_REVIEW_INACTIVE': {
         const reviews: ReviewModel[] = clone(state.reviews);
         const index = reviews.findIndex(review => review.Id === action.id);
         if (index > -1) {
            reviews[index].IsInactive = action.isInactive;
            return Object.assign({}, state, {
               reviews: reviews,
               currentReview: state.reviewId && reviews.find(r => r.Id === state.reviewId)
            });
         }
         return state;
      }
      case 'EDIT_COMMENT': {
         const stateToAssign = getNormalizedCommentState(state.comments, [action.comment], state.selectedComment, state.users,
            state.searchText, state.hideResolved, state.currentReviewVersion.Id, state.playTime);

         return Object.assign({}, state, stateToAssign);
      }
      case 'SET_COMMENT_SEARCH_TEXT': {
         const topLevelComments = getVisibleRootCommentsAndReplies(state.comments, action.searchText, state.hideResolved, state.currentReviewVersion.Id);
         const visibleComments = getVisibleAllCommentsAndReplies(state.comments, action.searchText, state.hideResolved, state.currentReviewVersion.Id);
         const markers = markersFromComments(visibleComments);
         const selectedComment = getCommentToSelectByTime(state.playTime, topLevelComments, state.hideResolved, state.selectedComment) || {};

         return Object.assign({}, state, {
            searchText: action.searchText,
            visibleComments: visibleComments,
            markers: markers,
            selectedComment,
            topLevelComments
         });
      }
      case 'SET_COMMENT_FILTER': {
         const topLevelComments = getVisibleRootCommentsAndReplies(state.comments, state.searchText, action.hideResolved, state.currentReviewVersion.Id);
         const visibleComments = getVisibleAllCommentsAndReplies(state.comments, state.searchText, action.hideResolved, state.currentReviewVersion.Id);
         const markers = markersFromComments(visibleComments);

         const selectedComment = getCommentToSelectByTime(state.playTime, topLevelComments, action.hideResolved, state.selectedComment);

         return Object.assign({}, state, {
            hideResolved: action.hideResolved,
            visibleComments: visibleComments,
            markers: markers,
            selectedComment,
            topLevelComments
         });
      }
      case 'SET_VIDEO_DURATION': {
         const playDuration = action.playDuration;

         const comments: CommentModel[] = clone(state.comments);
         comments.filter(c => c.time > playDuration).forEach(c => {
            c.time = playDuration;
         });

         const topLevelComments = getVisibleRootCommentsAndReplies(comments, state.searchText, state.hideResolved, state.currentReviewVersion.Id);
         const visibleComments = getVisibleAllCommentsAndReplies(comments, state.searchText, state.hideResolved, state.currentReviewVersion.Id);
         const markers = markersFromComments(visibleComments);

         return Object.assign({}, state, {
            comments,
            markers,
            visibleComments,
            playDuration,
            topLevelComments
         });
      }
      case 'SET_REVIEW_ID': {
         const currentReview = state.reviews.find(r => r.Id === action.id);
         const versionNumber = action.versionNumber ? action.versionNumber : currentReview.ReviewVersions.filter(ver => !ver.IsDeleted).pop().VersionNumber;
         const reviewVersion = currentReview && currentReview.ReviewVersions.find(v => v.VersionNumber === versionNumber);
         return Object.assign({}, state, {
            currentReviewVersionNumber: versionNumber,
            reviewId: action.id,
            hideResolved: loadResolutionFilter(action.id),
            currentReview: currentReview
         }, reviewVersion && {
            currentReviewVersion: reviewVersion
         }, state.currentUserId && {
            isOwner: hasOwnerPermissions(state.currentUserId, currentReview)
         });
      }
      case 'SET_EDITING_COMMENT_ID': {
         const editingComment = state.comments.find(c => c.id === action.id);
         return Object.assign({}, state, {
            editingComment: editingComment || {},
            annotationMode: AnnotationModes.none,
            hideAnnotation: false
         });
      }
      case 'SET_AUTHORIZATION': {
         const quotas: QuotasModel = nullQuotasModel();
         const authorizationResponse: AuthorizationsResponseModel = action.authorization;
         if (authorizationResponse && authorizationResponse.quotas) {
            quotas.reviews = authorizationResponse.quotas.Reviews || 0;
            quotas.reviewers = authorizationResponse.quotas.Reviewers || 0;
         }

         const authorizedActions: AuthorizedActionsModel = nullAuthorizedActionModel();
         if (action.authorization.actions) {
            action.authorization.actions.forEach((authorizationAction: string) => {
               switch (authorizationAction) {
                  case Constants.entitlements.permissions.createReview:
                     authorizedActions.canCreateReviews = true;
                     break;
                  case Constants.entitlements.permissions.startTrial:
                     authorizedActions.canStartTrial = true;
                     break;
               }
            });
         }
         const authorization: AuthorizationModel = {quotas: quotas, authorizedActions: authorizedActions};
         return Object.assign({}, state, {authorization: authorization});
      }
      case 'SET_ENTITLEMENT_STATUS': {
         let daysLeft = -1;

         if (action.entitlements.expirationDate) {
            const difference = action.entitlements.expirationDate.diff(moment().utc());
            const hours = moment.duration(difference).asHours();
            const roundedHours = Math.floor(hours); // Rounded hours lets us ignore extra minutes to compensate for clock skew
            if (roundedHours >= 24) {
               daysLeft = Math.ceil(roundedHours / 24);
            } else {
               daysLeft = hours > 0 ? 1 : 0;
            }
         }

         return Object.assign({}, state, {
            entitlements: action.entitlements,
            hasUsedTrial: action.entitlements.status !== '',
            daysLeft: daysLeft
         });
      }
      case 'SET_NOTIFICATION_SETTINGS': {
         return Object.assign({}, state, {
            commentNotificationSettings: filterNotificationSetting(action.notificationSettings, NotificationTypes.NewComment),
            replyNotificationSettings: filterNotificationSetting(action.notificationSettings, NotificationTypes.NewCommentReply)
         });
      }
      case 'SET_REVIEW_SEARCH_TEXT': {
         const reviewsToSearch = state.dashboardFilter !== DashboardFilter.all ? state.filteredReviews : state.reviews;
         const searchedReviews = getFilteredReviews(clone(reviewsToSearch), state.dashboardFilter, action.searchText, state.currentUserId);
         return Object.assign({}, state, {reviewSearchText: action.searchText, searchedReviews: searchedReviews});
      }
      case 'SET_DASHBOARD_FILTER': {
         const filteredReviews = getFilteredReviews(clone(state.reviews), action.dashboardFilter, '', state.currentUserId);
         const searchedReviews = state.reviewSearchText ? getFilteredReviews(clone(filteredReviews), action.dashboardFilter, state.reviewSearchText, state.currentUserId) : [];
         return Object.assign({}, state, {dashboardFilter: action.dashboardFilter, filteredReviews: filteredReviews, searchedReviews: searchedReviews});
      }
      case 'SET_REVIEWER_GROUPS': {
         return Object.assign({}, state, {reviewerGroups: action.reviewerGroups});
      }
      case 'SET_SELECTED_GROUP': {
         return Object.assign({}, state, {selectedGroup: action.selectedGroup});
      }
      case 'SET_PAGE_IS_LOADING': {
         return Object.assign({}, state, {pageIsLoading: action.isLoading});
      }
      case 'SET_ENABLE_COMMENT_HOTKEY': {
         return Object.assign({}, state, {isCommentHotkeyEnabled: action.isCommentHotkeyEnabled});
      }
      case 'SET_MEDIA_DIMENSIONS': {
         return Object.assign({}, state, {mediaDimensions: action.mediaDimensions});
      }
      case 'REVIEW_FEEDBACK_FOCUSED': {
         const isUserLoggedIn = state.currentUser && state.currentUser.userId;
         const isCurrentReviewPrivacyTypeLink = state.currentReview && state.currentReview.PrivacyType === PrivacyTypes.link;
         return !isUserLoggedIn && isCurrentReviewPrivacyTypeLink ? Object.assign({}, state, {modalState: ModalStates.selfIdentify}) : state;
      }
      case 'SET_REVIEWS_SORT_METRIC': {
         if (state.reviews?.length === state.totalReviewsCount) {
            const sortedReviews = getSortedReviews(clone(state.reviews), action.reviewsSortMetric);
            const sortedFilteredReviews = getSortedReviews(clone(state.filteredReviews), action.reviewsSortMetric);
            const sortedSearchedReviews = getSortedReviews(clone(state.searchedReviews), action.reviewsSortMetric);

            return Object.assign({}, state, {reviewsSortMetric: action.reviewsSortMetric, reviews: sortedReviews, filteredReviews: sortedFilteredReviews, searchedReviews: sortedSearchedReviews});
         }

         return Object.assign({}, state, {reviewsSortMetric: action.reviewsSortMetric});
      }
      case 'SET_QUIZ_STATE': {
         return Object.assign({}, state, {quizState: action.quizState});
      }
      case 'SET_TOTAL_REVIEWS_COUNT': {
         return Object.assign({}, state, {totalReviewsCount: action.totalReviewsCount});
      }
      case 'SET_TOTAL_OWNED_REVIEWS_COUNT': {
         return Object.assign({}, state, {totalOwnedReviewsCount: action.totalOwnedReviewsCount});
      }
      case 'SET_TOTAL_NOT_OWNED_REVIEWS_COUNT': {
         return Object.assign({}, state, {totalNotOwnedReviewsCount: action.totalNotOwnedReviewsCount});
      }
      default: {
         return state;
      }
   }
};
